/**
 * Case List — table view of all cases with filters, search & pagination.
 */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { CaseManagementService } from '../case-management.service';
import {
  LegalCase, CaseStatus, CaseType, CasePriority,
  CASE_TYPE_LABELS, CASE_STATUS_LABELS,
  CASE_STATUS_COLORS, CASE_PRIORITY_COLORS
} from '../case-management.model';

@Component({
  selector: 'sp-case-list',
  templateUrl: './case-list.component.html',
  styleUrls: ['./case-list.component.scss']
})
export class CaseListComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  isLoading = true;
  allCases: LegalCase[]  = [];
  cases: LegalCase[]     = [];

  // Filter state
  searchControl = new FormControl('');
  statusFilter:   CaseStatus | 'ALL'   = 'ALL';
  typeFilter:     CaseType   | 'ALL'   = 'ALL';
  priorityFilter: CasePriority | 'ALL' = 'ALL';
  assigneeFilter: string = 'ALL';
  overdueOnly = false;

  // Pagination
  currentPage = 1;
  pageSize    = 15;

  // Sorting
  sortField: 'openedDate' | 'nextEventDate' | 'exposure' | 'priority' = 'priority';
  sortDir:   'asc' | 'desc' = 'asc';

  assignees: string[] = [];

  readonly TYPE_LABELS    = CASE_TYPE_LABELS;
  readonly STATUS_LABELS  = CASE_STATUS_LABELS;
  readonly STATUS_COLORS  = CASE_STATUS_COLORS;
  readonly PRIORITY_COLORS = CASE_PRIORITY_COLORS;
  readonly statusOptions:   ('ALL'|CaseStatus)[]   = ['ALL','ACTIVE','PENDING','ON_HOLD','CLOSED'];
  readonly typeOptions:     ('ALL'|CaseType)[]     = ['ALL','SUBROGATION','DEFENSE','RECOVERY','FRAUD','EMPLOYMENT','CONTRACT','REGULATORY','OTHER'];
  readonly priorityOptions: ('ALL'|CasePriority)[] = ['ALL','HIGH','MEDIUM','LOW'];

  constructor(
    private caseService: CaseManagementService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.assignees = ['ALL', ...this.caseService.getDistinctAssignees()];
    this.searchControl.valueChanges
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());
    this.loadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAll(): void {
    this.isLoading = true;
    this.caseService.listCases()
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => {
        this.allCases = list;
        this.applyFilters();
        this.isLoading = false;
      });
  }

  applyFilters(): void {
    let filtered = [...this.allCases];

    const term = (this.searchControl.value || '').toLowerCase().trim();
    if (term) {
      filtered = filtered.filter(c =>
        c.id.toLowerCase().includes(term) ||
        c.title.toLowerCase().includes(term) ||
        c.opposingParty.toLowerCase().includes(term) ||
        (c.sourceRef || '').toLowerCase().includes(term) ||
        (c.courtCaseNumber || '').toLowerCase().includes(term) ||
        c.assignedTo.toLowerCase().includes(term)
      );
    }
    if (this.statusFilter   !== 'ALL') filtered = filtered.filter(c => c.status === this.statusFilter);
    if (this.typeFilter     !== 'ALL') filtered = filtered.filter(c => c.type === this.typeFilter);
    if (this.priorityFilter !== 'ALL') filtered = filtered.filter(c => c.priority === this.priorityFilter);
    if (this.assigneeFilter !== 'ALL') filtered = filtered.filter(c => c.assignedTo === this.assigneeFilter);
    if (this.overdueOnly) {
      const now = new Date();
      filtered = filtered.filter(c =>
        c.status !== 'CLOSED' && c.nextEventDate && c.nextEventDate < now
      );
    }

    this.sortInPlace(filtered);
    this.cases = filtered;
    this.currentPage = 1;
  }

  private sortInPlace(list: LegalCase[]): void {
    const dir = this.sortDir === 'asc' ? 1 : -1;
    const priorityRank = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    list.sort((a, b) => {
      let cmp = 0;
      switch (this.sortField) {
        case 'priority':
          cmp = priorityRank[a.priority] - priorityRank[b.priority]; break;
        case 'openedDate':
          cmp = a.openedDate.getTime() - b.openedDate.getTime(); break;
        case 'nextEventDate': {
          const an = a.nextEventDate?.getTime() ?? Infinity;
          const bn = b.nextEventDate?.getTime() ?? Infinity;
          cmp = an - bn; break;
        }
        case 'exposure':
          cmp = a.exposureAmount - b.exposureAmount; break;
      }
      return cmp * dir;
    });
  }

  onSort(field: 'openedDate'|'nextEventDate'|'exposure'|'priority'): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = field === 'exposure' ? 'desc' : 'asc';
    }
    this.applyFilters();
  }

  resetFilters(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.statusFilter = 'ALL';
    this.typeFilter = 'ALL';
    this.priorityFilter = 'ALL';
    this.assigneeFilter = 'ALL';
    this.overdueOnly = false;
    this.applyFilters();
  }

  // Pagination helpers
  get pageCount(): number {
    return Math.max(1, Math.ceil(this.cases.length / this.pageSize));
  }
  get pagedCases(): LegalCase[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.cases.slice(start, start + this.pageSize);
  }
  prevPage(): void { if (this.currentPage > 1) this.currentPage--; }
  nextPage(): void { if (this.currentPage < this.pageCount) this.currentPage++; }
  goToPage(p: number): void { this.currentPage = Math.min(Math.max(1, p), this.pageCount); }

  // Navigation
  openCase(c: LegalCase): void {
    this.router.navigate(['/case-management/cases', c.id]);
  }
  openNewCase(): void {
    this.router.navigate(['/case-management/new']);
  }
  goDashboard(): void {
    this.router.navigate(['/case-management']);
  }

  // Format helpers
  formatCurrency(value: number, compact = false): string {
    if (compact && Math.abs(value) >= 1_000_000) return `KES ${(value/1_000_000).toFixed(1)}M`;
    if (compact && Math.abs(value) >= 1_000)     return `KES ${(value/1_000).toFixed(0)}K`;
    return new Intl.NumberFormat('en-KE', { style:'currency', currency:'KES', minimumFractionDigits:0, maximumFractionDigits:0 }).format(value);
  }
  formatDate(d?: Date): string {
    if (!d) return '—';
    return d.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
  }
  daysUntil(d?: Date): number | null {
    if (!d) return null;
    return Math.ceil((d.getTime() - new Date().getTime()) / 86400000);
  }
  nextEventClass(d?: Date): string {
    const n = this.daysUntil(d);
    if (n === null) return 'no-event';
    if (n < 0)  return 'overdue';
    if (n <= 2) return 'critical';
    if (n <= 7) return 'warning';
    return 'normal';
  }
  trackById(_: number, c: LegalCase): string { return c.id; }
}
